import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive, ArchiveRestore, ArrowUpRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { EmptyState, Panel, PanelHeader, SkeletonTable } from '@/components/app/States';
import { Note } from '@/components/app/Badges';
import { Field, FormSheet, HistoryList, RefSelect, SelectField, ServiceStatusPill, SheetSection, StatusPill, inputClass } from '@/components/app/DriftUI';
import { adminCreateMachine, adminDeleteMachine, adminListMachines, adminPatchMachine, serviceLine } from '@/lib/driftApi';
import { fmtNumber } from '@/domain/numbers';
import { cn } from '@/lib/utils';

/*
  Maskinregister i Innstillinger: opprett maskin, rediger grunnopplysninger (internnr, navn, type, årsmodell, tellertype, notat),
  arkiver/reaktiver. Plassering, registerets teller, ansvarlig og eksempelplan for service redigeres på maskinkortet (samme data).
  Registerets timeteller/km-stand (livstid) blandes aldri med registrert maskintid fra timeføring eller telematikk.
*/

const FILTERS = [
  { id: 'aktive', label: 'Aktive' },
  { id: 'arkiverte', label: 'Arkiverte' },
  { id: 'alle', label: 'Alle' },
];
const METER_TYPES = [
  { value: 'h', label: 'Timeteller (t)' },
  { value: 'km', label: 'Km-stand' },
];
const SERVICE_KINDS = [
  { value: 'h', label: 'Etter timer' },
  { value: 'km', label: 'Etter km' },
  { value: 'dato', label: 'Etter dato (dager)' },
];

const emptyCreate = () => ({ internalNo: '', name: '', type: '', modelYear: '', location: '', meterType: 'h', meter: '', responsibleId: '', serviceKind: '', serviceInterval: '', note: '', reason: '' });
const toEdit = (m) => ({ internalNo: m.internalNo ?? '', name: m.name ?? '', type: m.type ?? '', modelYear: m.modelYear != null ? String(m.modelYear) : '', meterType: m.meterType ?? 'h', note: m.note ?? '', reason: '' });

function validate(f, create) {
  const e = {};
  if (!f.internalNo.trim()) e.internalNo = 'Oppgi internnr';
  if (f.name.trim().length < 2) e.name = 'Oppgi navn';
  if (!f.type) e.type = 'Velg maskintype';
  if (f.modelYear && !(Number(f.modelYear) >= 1980 && Number(f.modelYear) <= 2100)) e.modelYear = 'Årsmodell mellom 1980 og 2100';
  if (create && f.meter !== '' && !(Number(f.meter) >= 0)) e.meter = 'Telleren kan ikke være negativ';
  if (create && f.serviceKind && !(Number(f.serviceInterval) > 0)) e.serviceInterval = 'Oppgi intervall';
  return e;
}

export function AdminMaskiner({ refs, search = '', onLoaded }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [filter, setFilter] = useState('aktive');
  const [sheet, setSheet] = useState(null); // null | { mode: 'ny' } | { mode: 'rediger', machine }
  const [form, setForm] = useState(emptyCreate);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const testId = 'admin-machines';

  const load = useCallback(async () => {
    setStatus((s) => (s === 'ready' ? 'refreshing' : 'loading'));
    try {
      const d = await adminListMachines();
      setData(d);
      setStatus('ready');
      onLoaded?.('machines', d);
    } catch {
      setStatus('error');
    }
  }, [onLoaded]);

  useEffect(() => {
    load();
  }, [load]);

  const machines = useMemo(() => {
    const all = data?.machines ?? [];
    const q = search.trim().toLowerCase();
    return all
      .filter((m) => (filter === 'alle' ? true : filter === 'arkiverte' ? m.active === false : m.active !== false))
      .filter((m) => !q || [m.internalNo, m.name, m.typeLabel, m.location, m.machineId, m.responsible?.name].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [data, filter, search]);
  const counts = useMemo(() => {
    const all = data?.machines ?? [];
    return { aktive: all.filter((m) => m.active !== false).length, arkiverte: all.filter((m) => m.active === false).length, alle: all.length };
  }, [data]);

  const current = sheet?.mode === 'rediger' ? data?.machines.find((m) => m.machineId === sheet.machine.machineId) ?? sheet.machine : null;
  const types = (data?.machineTypes ?? refs?.machineTypes ?? []).map((t) => ({ value: t.id, label: t.label }));

  const openNew = () => {
    setErrors({});
    setForm(emptyCreate());
    setSheet({ mode: 'ny' });
  };
  const openEdit = (m) => {
    setErrors({});
    setForm(toEdit(m));
    setSheet({ mode: 'rediger', machine: m });
  };
  const replaceMachine = (updated) => setData((d) => (d ? { ...d, machines: d.machines.map((m) => (m.machineId === updated.machineId ? { ...m, ...updated } : m)) } : d));

  const save = async () => {
    const create = sheet.mode === 'ny';
    const e = validate(form, create);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      if (create) {
        const body = {
          internalNo: form.internalNo.trim(), name: form.name.trim(), type: form.type, modelYear: form.modelYear ? Number(form.modelYear) : null, location: form.location || null, meterType: form.meterType,
          meter: form.meter === '' ? null : Number(form.meter), responsibleId: form.responsibleId || null, serviceKind: form.serviceKind || null, serviceInterval: form.serviceKind ? Number(form.serviceInterval) : null, note: form.note || null, reason: form.reason || null,
        };
        const created = await adminCreateMachine(body);
        toast.success('Maskin opprettet', { description: `${created.internalNo} · ${created.name} – kan velges i timeføring og saker.` });
        setSheet(null);
        load();
      } else {
        const body = {};
        const cmp = toEdit(current);
        for (const k of ['internalNo', 'name', 'type', 'modelYear', 'meterType', 'note']) {
          if (form[k] !== cmp[k]) body[k] = k === 'modelYear' ? (form[k] ? Number(form[k]) : null) : form[k] || null;
        }
        if (!Object.keys(body).length) {
          toast.info('Ingen endringer å lagre');
          return;
        }
        if (form.reason) body.reason = form.reason;
        const updated = await adminPatchMachine(current.machineId, body);
        replaceMachine(updated);
        toast.success('Endringen er lagret', { description: 'Før → etter ligger i maskinens historikk.' });
        setSheet(null);
        load();
      }
    } catch (err) {
      toast.error('Kunne ikke lagre', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (active) => {
    setSaving(true);
    try {
      const updated = await adminPatchMachine(current.machineId, { active, reason: form.reason || null });
      replaceMachine(updated);
      toast.success(active ? 'Maskinen er reaktivert' : 'Maskinen er arkivert', { description: active ? 'Vises igjen i maskinparken og kan velges.' : 'Ute av maskinparken og nye valg. Gamle føringer og saker beholder maskinen.' });
      setSheet(null);
      load();
    } catch (err) {
      toast.error('Kunne ikke endre status', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await adminDeleteMachine(confirmDelete.machineId);
      toast.success('Maskinen er slettet');
      setSheet(null);
      load();
    } catch (err) {
      toast.error('Kunne ikke slette', { description: err.message });
    } finally {
      setSaving(false);
      setConfirmDelete(null);
    }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const meterText = (m) => (m.meter == null ? 'Teller ikke registrert' : `${fmtNumber(m.meter, 0)} ${m.meterType === 'km' ? 'km' : 't'}`);

  return (
    <Panel className="min-w-0" data-testid={`${testId}-panel`}>
      <PanelHeader
        title="Maskiner"
        description={data ? `${counts.aktive} aktive · ${counts.arkiverte} arkiverte. Teller = registerets livstidsverdi, ikke registrert maskintid.` : undefined}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-[8px] border border-[var(--hr-border)] bg-white p-0.5" role="tablist" aria-label="Vis" data-testid={`${testId}-filter`}>
              {FILTERS.map((f) => (
                <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} onClick={() => setFilter(f.id)} className={cn('h-7 rounded-[6px] px-2.5 text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', filter === f.id ? 'bg-[var(--hr-heading)] text-white' : 'text-[var(--hr-muted)] hover:bg-[var(--hr-surface-2)] hover:text-[var(--hr-heading)]')} data-testid={`${testId}-filter-${f.id}`}>
                  {f.label} <span className={cn('hr-tabular', filter === f.id ? 'text-white/70' : 'text-[var(--hr-muted-2)]')}>{counts[f.id]}</span>
                </button>
              ))}
            </div>
            <Button onClick={openNew} className="h-9 rounded-[8px] bg-[var(--hr-heading)] px-3 text-[14px] font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]" data-testid={`${testId}-new-button`}>
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Ny maskin
            </Button>
          </div>
        }
      />
      {status === 'loading' && <div className="p-4"><SkeletonTable rows={5} /></div>}
      {status === 'error' && (
        <div className="p-4">
          <Note tone="amber" role="alert" data-testid={`${testId}-error`}>
            Kunne ikke hente maskinregisteret. <Button variant="link" className="h-auto px-1" onClick={load} data-testid={`${testId}-retry`}>Prøv igjen</Button>
          </Note>
        </div>
      )}
      {(status === 'ready' || status === 'refreshing') && machines.length === 0 && (
        <div className="p-4">
          <EmptyState title={search ? 'Ingen treff' : filter === 'arkiverte' ? 'Ingen arkiverte maskiner' : 'Ingen maskiner'} description={search ? 'Prøv et annet søkeord.' : 'Arkiverte maskiner havner her og kan reaktiveres.'} onReset={filter !== 'alle' ? () => setFilter('alle') : undefined} resetLabel="Vis alle" className="py-8" />
        </div>
      )}
      {(status === 'ready' || status === 'refreshing') && machines.length > 0 && (
        <ul className={cn('divide-y divide-[var(--hr-hairline)]', status === 'refreshing' && 'opacity-70 transition-opacity duration-150')} data-testid={`${testId}-list`}>
          {machines.map((m) => (
            <li key={m.machineId}>
              <button type="button" onClick={() => openEdit(m)} className="group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)] sm:px-5" data-testid={`${testId}-row-${m.machineId}`}>
                <span className="hr-tabular w-[56px] shrink-0 text-[13px] text-[var(--hr-muted)]">{m.internalNo}</span>
                <span className="min-w-0 flex-1">
                  <span className={cn('block truncate text-[14px] font-medium', m.active === false ? 'text-[var(--hr-muted)]' : 'text-[var(--hr-heading)]')}>{m.name}</span>
                  <span className="block truncate text-[13px] text-[var(--hr-muted)]">
                    {[m.typeLabel, m.modelYear, m.location, meterText(m)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="hidden shrink-0 text-[13px] text-[var(--hr-muted-2)] lg:block">{serviceLine(m.service, { short: true })}</span>
                <span className="hidden shrink-0 text-[13px] text-[var(--hr-muted-2)] md:block" data-testid={`${testId}-usage-${m.machineId}`}>{m.usage?.total ? `${m.usage.total} registreringer` : 'Ikke i bruk'}</span>
                {m.active === false ? <StatusPill tone="neutral">Arkivert</StatusPill> : <ServiceStatusPill status={m.service.status} />}
                <Pencil className="hidden h-4 w-4 shrink-0 text-[var(--hr-muted-2)] opacity-0 transition-opacity duration-150 group-hover:opacity-100 sm:block" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <FormSheet
        open={!!sheet}
        onOpenChange={(o) => !o && setSheet(null)}
        title={sheet?.mode === 'ny' ? 'Ny maskin' : current ? `${current.internalNo} · ${current.name}` : ''}
        description={sheet?.mode === 'ny' ? 'Internnr, navn og type først. Teller og eksempelplan kan settes nå eller på maskinkortet senere.' : current ? `${current.typeLabel}${current.modelYear ? ` · ${current.modelYear}` : ''} · ${current.usage?.total ? `${current.usage.total} registreringer` : 'ikke i bruk'}` : undefined}
        testId={`${testId}-sheet`}
        footer={
          sheet && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {current && (current.active === false ? (
                  <Button variant="outline" className="h-10 rounded-[10px] bg-white" onClick={() => setActive(true)} disabled={saving} data-testid={`${testId}-reactivate-button`}>
                    <ArchiveRestore className="mr-1.5 h-4 w-4" aria-hidden="true" /> Reaktiver
                  </Button>
                ) : (
                  <Button variant="outline" className="h-10 rounded-[10px] bg-white" onClick={() => setActive(false)} disabled={saving} data-testid={`${testId}-archive-button`}>
                    <Archive className="mr-1.5 h-4 w-4" aria-hidden="true" /> Arkiver
                  </Button>
                ))}
                {current && !current.seeded && !current.usage?.total && (
                  <Button variant="ghost" className="h-10 rounded-[10px] text-[var(--hr-muted)] hover:text-[var(--hr-red)]" onClick={() => setConfirmDelete(current)} disabled={saving} data-testid={`${testId}-delete-button`}>
                    <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" /> Slett
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" className="h-10 rounded-[10px]" onClick={() => setSheet(null)} data-testid={`${testId}-cancel-button`}>Avbryt</Button>
                <Button className="h-10 rounded-[10px] bg-[var(--hr-heading)] px-4 font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]" onClick={save} disabled={saving} data-testid={`${testId}-save-button`}>
                  {saving ? 'Lagrer …' : 'Lagre'}
                </Button>
              </div>
            </div>
          )
        }
      >
        {sheet && (
          <div className="space-y-4">
            <div className="grid grid-cols-[110px_1fr] gap-3">
              <Field label="Internnr" htmlFor="mk-internal" required error={errors.internalNo}>
                <Input id="mk-internal" value={form.internalNo} onChange={(e) => set('internalNo')(e.target.value)} maxLength={12} placeholder="E03" className={cn(inputClass, 'hr-tabular uppercase')} data-testid={`${testId}-internalNo`} />
              </Field>
              <Field label="Navn" htmlFor="mk-name" required error={errors.name}>
                <Input id="mk-name" value={form.name} onChange={(e) => set('name')(e.target.value)} maxLength={60} placeholder="Gravemaskin 3" className={inputClass} data-testid={`${testId}-name`} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type" htmlFor="mk-type" required error={errors.type}>
                <SelectField id="mk-type" value={form.type} onValueChange={set('type')} options={types} testId={`${testId}-type`} />
              </Field>
              <Field label="Årsmodell" htmlFor="mk-year" error={errors.modelYear}>
                <Input id="mk-year" type="number" inputMode="numeric" min={1980} max={2100} value={form.modelYear} onChange={(e) => set('modelYear')(e.target.value)} placeholder="2020" className={inputClass} data-testid={`${testId}-modelYear`} />
              </Field>
            </div>
            <Field label="Tellertype" htmlFor="mk-metertype" hint="Registerets livstidsteller – ikke registrert maskintid fra timeføring og ikke telematikk.">
              <SelectField id="mk-metertype" value={form.meterType} onValueChange={set('meterType')} options={METER_TYPES} testId={`${testId}-meterType`} />
            </Field>
            {sheet.mode === 'ny' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={form.meterType === 'km' ? 'Km-stand nå' : 'Timeteller nå'} htmlFor="mk-meter" error={errors.meter} hint="Valgfritt">
                    <Input id="mk-meter" type="number" inputMode="numeric" min={0} value={form.meter} onChange={(e) => set('meter')(e.target.value)} placeholder="0" className={inputClass} data-testid={`${testId}-meter`} />
                  </Field>
                  <Field label="Plassering" htmlFor="mk-location" hint="Valgfritt">
                    <Input id="mk-location" value={form.location} onChange={(e) => set('location')(e.target.value)} maxLength={80} placeholder="Bruddet" className={inputClass} data-testid={`${testId}-location`} />
                  </Field>
                </div>
                <Field label="Ansvarlig" htmlFor="mk-resp" hint="Valgfritt – kun aktive medarbeidere">
                  <RefSelect id="mk-resp" value={form.responsibleId} onValueChange={set('responsibleId')} allowEmpty emptyLabel="Ikke satt" list={refs?.employees} getHint={(x) => x.role} testId={`${testId}-responsibleId`} />
                </Field>
                <SheetSection title="Eksempelplan for service" description="Valgfritt. Uten «sist utført» vises status «Ukjent» til første service registreres – aldri en falsk OK.">
                  <div className="grid grid-cols-2 gap-3">
                    <SelectField value={form.serviceKind} onValueChange={set('serviceKind')} allowEmpty emptyLabel="Ingen plan" options={SERVICE_KINDS} testId={`${testId}-serviceKind`} />
                    <Input type="number" inputMode="numeric" min={1} value={form.serviceInterval} onChange={(e) => set('serviceInterval')(e.target.value)} placeholder={form.serviceKind === 'dato' ? 'Dager' : 'Intervall'} aria-label="Intervall" disabled={!form.serviceKind} className={inputClass} data-testid={`${testId}-serviceInterval`} />
                  </div>
                  {errors.serviceInterval && <p className="mt-1 text-[13px] text-[var(--hr-red)]" role="alert">{errors.serviceInterval}</p>}
                </SheetSection>
              </>
            )}
            <Field label="Notat" htmlFor="mk-note" hint="Valgfritt">
              <Textarea id="mk-note" value={form.note} onChange={(e) => set('note')(e.target.value)} rows={2} maxLength={300} className="min-h-[64px] rounded-[10px] border-[var(--hr-border)] text-[16px] shadow-none lg:text-[14px]" data-testid={`${testId}-note`} />
            </Field>
            {current && (
              <div className="rounded-[10px] border border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] px-3 py-2.5 text-[13px] leading-5 text-[var(--hr-muted)]" data-testid={`${testId}-card-hint`}>
                Plassering {current.location ?? '–'} · {meterText(current)} · ansvarlig {current.responsible?.name ?? '–'} · {serviceLine(current.service)}.{' '}
                <Link to={`/maskiner/${encodeURIComponent(current.machineId)}`} className="inline-flex items-center gap-0.5 font-medium text-[var(--hr-accent-text)] underline-offset-2 hover:underline" data-testid={`${testId}-open-card`}>
                  Endres på maskinkortet <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
                </Link>
              </div>
            )}
            <SheetSection title={current ? 'Årsak til endringen' : 'Merknad'} description="Valgfritt. Lagres i historikken sammen med før → etter.">
              <Input value={form.reason} onChange={(e) => set('reason')(e.target.value)} maxLength={300} placeholder="F.eks. «Feil årsmodell ved registrering»" className={inputClass} data-testid={`${testId}-reason`} />
            </SheetSection>
            {current && (
              <SheetSection title="Historikk" description="Før → etter, merket «Kontor (eksempeldata)».">
                <HistoryList history={current.history} testId={`${testId}-history`} />
              </SheetSection>
            )}
          </div>
        )}
      </FormSheet>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid={`${testId}-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette maskinen?</AlertDialogTitle>
            <AlertDialogDescription>Maskinen er ikke i bruk og fjernes helt fra registeret. Maskiner med timer eller saker kan bare arkiveres.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testId}-confirm-cancel`}>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid={`${testId}-confirm-ok`}>Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
}
